import React, { useEffect } from 'react';
import { AppProvider, useApp, AppView } from './context/AppContext';
import { ThemeProvider } from './context/ThemeContext';
import { Navbar } from './components/layout/Navbar';
import { Footer } from './components/layout/Footer';
import { HomePage } from './pages/HomePage';
import { CustomerAuthPage } from './pages/auth/CustomerAuthPage';
import { ProfessionalAuthPage } from './pages/auth/ProfessionalAuthPage';
import { AdminAuthPage } from './pages/auth/AdminAuthPage';
import { PasswordResetPage } from './pages/auth/PasswordResetPage';
import { CustomerDashboard } from './pages/customer/CustomerDashboard';
import { PostJobPage } from './pages/customer/PostJobPage';
import { QuoteComparisonPage } from './pages/customer/QuoteComparisonPage';
import { ProfessionalDashboard } from './pages/professional/ProfessionalDashboard';
import { AvailableJobsPage } from './pages/professional/AvailableJobsPage';
import { ProfessionalProfilePage } from './pages/professional/ProfessionalProfilePage';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { MessagesPage } from './pages/communication/MessagesPage';
import { NotificationsPage } from './pages/communication/NotificationsPage';
import { ProfessionalSubscriptionPage } from './pages/professional/ProfessionalSubscriptionPage';
import { FindProfessionalPage } from './pages/public/FindProfessionalPage';
import { HowItWorksPage } from './pages/public/HowItWorksPage';
import { BecomeProfessionalPage } from './pages/public/BecomeProfessionalPage';
import { CustomerGuidePage } from './pages/public/CustomerGuidePage';
import { TrustVerificationPage } from './pages/public/TrustVerificationPage';
import { FaqPage } from './pages/public/FaqPage';
import { AboutPage } from './pages/public/AboutPage';
import { ContactPage } from './pages/public/ContactPage';
import { PrivacyPolicyPage } from './pages/public/PrivacyPolicyPage';
import { TermsConditionsPage } from './pages/public/TermsConditionsPage';
import { NotFoundPage } from './pages/public/NotFoundPage';
import { JobDetailsPage } from './pages/public/JobDetailsPage';
import { TelegramChatButton } from './components/TelegramChatButton';
import { MobileBottomNav } from './components/layout/MobileBottomNav';
import { AdminAccessDenied } from './components/admin/AdminAccessDenied';
import { AuthSelectionModal } from './components/auth/AuthSelectionModal';
import { AutoPushSubscriptionPrompt } from './components/notifications/AutoPushSubscriptionPrompt';
import { PWAInstallManager } from './components/pwa/PWAInstallManager';
import { updateDocumentSEO } from './utils/seo';
import { supabase } from './lib/supabase';

const RouteGuard: React.FC<{
  children: React.ReactNode;
  allowedRoles?: ('customer' | 'professional' | 'admin')[];
  authTarget: 'customer' | 'professional' | 'admin';
  targetView: AppView;
}> = ({ children, allowedRoles, authTarget, targetView }) => {
  const { currentUser, authLoading, navigateTo, logoutUser } = useApp();

  if (authLoading) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center space-y-4">
        <div className="w-10 h-10 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">Checking your account...</p>
      </div>
    );
  }

  if (!currentUser) {
    const authView =
      authTarget === 'admin'
        ? 'admin_auth'
        : authTarget === 'professional'
        ? 'pro_auth'
        : 'customer_auth';

    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center space-y-4 max-w-md mx-auto">
        <div className="w-12 h-12 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 rounded-2xl flex items-center justify-center mx-auto text-xl font-bold border border-emerald-100 dark:border-emerald-900/50">
          🔒
        </div>
        <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">Sign In Required</h2>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Please sign in to your {authTarget === 'admin' ? 'administrator' : authTarget === 'professional' ? 'professional' : 'customer'} account to access this section.
        </p>
        <button
          onClick={() => navigateTo(authView, { authMode: 'login', returnView: targetView })}
          className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer"
        >
          Sign In to Continue
        </button>
      </div>
    );
  }

  if (authTarget === 'admin' && currentUser.role !== 'admin') {
    return <AdminAccessDenied />;
  }

  if (allowedRoles && !allowedRoles.includes(currentUser.role) && currentUser.role !== 'admin') {
    const isProTryingCustomer = authTarget === 'customer' && currentUser.role === 'professional';
    const isCustomerTryingPro = authTarget === 'professional' && currentUser.role === 'customer';

    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center space-y-4 max-w-md mx-auto">
        <div className="w-14 h-14 bg-rose-50 dark:bg-rose-950/60 text-rose-600 dark:text-rose-400 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold border border-rose-100 dark:border-rose-900/50 shadow-xs">
          🚫
        </div>
        <div className="space-y-1">
          <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
            {isProTryingCustomer
              ? 'Access Refused: Professional Account'
              : isCustomerTryingPro
              ? 'Access Refused: Customer Account'
              : 'Access Restricted'}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
            {isProTryingCustomer ? (
              <>
                You cannot access or sign in to the Customer Dashboard using a Professional account. You are currently signed in as <strong>{currentUser.fullName}</strong> ({currentUser.email}).
              </>
            ) : isCustomerTryingPro ? (
              <>
                You cannot access or sign in to the Professional Dashboard using a Customer account. You are currently signed in as <strong>{currentUser.fullName}</strong> ({currentUser.email}).
              </>
            ) : (
              `This section is intended for ${allowedRoles.join(' or ')} accounts. You are currently signed in as a ${currentUser.role}.`
            )}
          </p>
          <p className="text-xs font-semibold text-slate-700 dark:text-slate-300 mt-2">
            {isProTryingCustomer
              ? 'Please go to your Professional Dashboard or use the Customer Login page to sign in with a Customer account.'
              : isCustomerTryingPro
              ? 'Please go to your Customer Dashboard or use the Professional Login page to sign in with a Professional account.'
              : 'Please navigate to your appropriate account section.'}
          </p>
        </div>

        <div className="flex flex-col gap-2.5 w-full pt-2">
          {isProTryingCustomer ? (
            <>
              <button
                onClick={() => navigateTo('pro_dashboard')}
                className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer"
              >
                Go to Professional Dashboard
              </button>
              <button
                onClick={async () => {
                  await logoutUser();
                  navigateTo('customer_auth', { authMode: 'login' });
                }}
                className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-semibold rounded-xl text-xs transition-all cursor-pointer"
              >
                Go to Customer Login Page
              </button>
            </>
          ) : isCustomerTryingPro ? (
            <>
              <button
                onClick={() => navigateTo('customer_dashboard')}
                className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer"
              >
                Go to Customer Dashboard
              </button>
              <button
                onClick={async () => {
                  await logoutUser();
                  navigateTo('pro_auth', { authMode: 'login' });
                }}
                className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-semibold rounded-xl text-xs transition-all cursor-pointer"
              >
                Go to Professional Login Page
              </button>
            </>
          ) : (
            <button
              onClick={() =>
                navigateTo(
                  currentUser.role === 'professional'
                    ? 'pro_dashboard'
                    : currentUser.role === 'admin'
                    ? 'admin_dashboard'
                    : 'customer_dashboard'
                )
              }
              className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer"
            >
              Go to My Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }

  const isProApprovalPending =
    currentUser.role === 'professional' &&
    (currentUser.verificationStatus === 'pending' ||
      currentUser.professionalApprovalStatus === 'pending');

  if (isProApprovalPending && (targetView === 'available_jobs' || targetView === 'pro_subscription')) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 text-center space-y-4 max-w-md mx-auto">
        <div className="w-14 h-14 bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold border border-amber-200 dark:border-amber-900/50 shadow-xs">
          ⏳
        </div>
        <h2 className="text-xl font-extrabold text-slate-900 dark:text-white">
          Professional approval pending
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
          Your professional application, trade details, and identity documents are currently under administrator review. You will gain full access to bid on available jobs once approved by a JobMatch NG Administrator.
        </p>
        <div className="flex flex-col gap-2 w-full pt-2">
          <button
            onClick={() => navigateTo('pro_dashboard')}
            className="w-full py-2.5 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs shadow-md transition-all cursor-pointer"
          >
            Return to Professional Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {isProApprovalPending && (
        <div className="bg-amber-50 dark:bg-amber-950/80 border-b border-amber-200 dark:border-amber-800 px-4 py-2.5 text-center">
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-200">
            <span className="font-extrabold">Professional approval pending:</span> Your identity documents and trade profile are currently under administrator review. Job bidding unlocks immediately upon approval.
          </p>
        </div>
      )}
      {children}
    </>
  );
};

const AppContent: React.FC = () => {
  const { currentView, viewParams } = useApp();

  // Dynamic document title & meta tags synchronization
  useEffect(() => {
    updateDocumentSEO(currentView, undefined, viewParams);
  }, [currentView, viewParams]);

  const renderCurrentView = () => {
    switch (currentView) {
      case 'home':
        return <HomePage />;
      case 'customer_auth':
        return <CustomerAuthPage />;
      case 'pro_auth':
        return <ProfessionalAuthPage />;
      case 'admin_auth':
        return <AdminAuthPage />;
      case 'password_reset':
        return <PasswordResetPage />;
      case 'customer_dashboard':
        return (
          <RouteGuard allowedRoles={['customer']} authTarget="customer" targetView="customer_dashboard">
            <CustomerDashboard />
          </RouteGuard>
        );
      case 'post_job':
        return (
          <RouteGuard allowedRoles={['customer']} authTarget="customer" targetView="post_job">
            <PostJobPage />
          </RouteGuard>
        );
      case 'quote_comparison':
        return (
          <RouteGuard allowedRoles={['customer']} authTarget="customer" targetView="quote_comparison">
            <QuoteComparisonPage />
          </RouteGuard>
        );
      case 'pro_dashboard':
        return (
          <RouteGuard allowedRoles={['professional']} authTarget="professional" targetView="pro_dashboard">
            <ProfessionalDashboard />
          </RouteGuard>
        );
      case 'available_jobs':
        return (
          <RouteGuard allowedRoles={['professional']} authTarget="professional" targetView="available_jobs">
            <AvailableJobsPage />
          </RouteGuard>
        );
      case 'pro_subscription':
        return (
          <RouteGuard allowedRoles={['professional']} authTarget="professional" targetView="pro_subscription">
            <ProfessionalSubscriptionPage />
          </RouteGuard>
        );
      case 'admin_dashboard':
        return (
          <RouteGuard allowedRoles={['admin']} authTarget="admin" targetView="admin_dashboard">
            <AdminDashboard />
          </RouteGuard>
        );
      case 'messages':
        return (
          <RouteGuard allowedRoles={['customer', 'professional', 'admin']} authTarget="customer" targetView="messages">
            <MessagesPage />
          </RouteGuard>
        );
      case 'notifications':
        return (
          <RouteGuard allowedRoles={['customer', 'professional', 'admin']} authTarget="customer" targetView="notifications">
            <NotificationsPage />
          </RouteGuard>
        );
      case 'pro_profile':
        return <ProfessionalProfilePage />;
      case 'find_pro':
        return <FindProfessionalPage />;
      case 'how_it_works':
        return <HowItWorksPage />;
      case 'become_pro':
        return <BecomeProfessionalPage />;
      case 'customer_guide':
        return <CustomerGuidePage />;
      case 'verification':
        return <TrustVerificationPage />;
      case 'faq':
        return <FaqPage />;
      case 'about':
        return <AboutPage />;
      case 'contact':
        return <ContactPage />;
      case 'privacy':
        return <PrivacyPolicyPage />;
      case 'terms':
        return <TermsConditionsPage />;
      case 'job_details':
        return <JobDetailsPage />;
      case 'not_found':
        return <NotFoundPage />;
      default:
        return <HomePage />;
    }
  };

  const isAdminView = currentView === 'admin_dashboard' || currentView === 'admin_auth';
  const isMessagesView = currentView === 'messages';

  return (
    <div className={`min-h-screen flex flex-col ${isAdminView ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100'} font-sans antialiased selection:bg-emerald-500 selection:text-white overflow-x-hidden transition-colors duration-200`}>
      {!isAdminView && <PWAInstallManager />}
      {!isAdminView && <Navbar />}
      <main className={isAdminView ? 'flex-1' : 'flex-1 pb-24 md:pb-0'}>
        {renderCurrentView()}
      </main>
      {!isAdminView && <Footer />}
      {!isAdminView && !isMessagesView && <TelegramChatButton />}
      {!isAdminView && <MobileBottomNav />}
      <AuthSelectionModal />
      {!isAdminView && <AutoPushSubscriptionPrompt />}
    </div>
  );
};

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
  errorId: string | null;
  showDetails: boolean;
  reportStatus: 'idle' | 'sending' | 'sent' | 'failed';
  userNotes: string;
}

class ErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      componentStack: null,
      errorId: null,
      showDetails: false,
      reportStatus: 'idle',
      userNotes: '',
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    const errorId = `ERR-JM-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    return {
      hasError: true,
      error,
      errorId,
      showDetails: false,
      reportStatus: 'sending',
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorId =
      this.state.errorId || `ERR-JM-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;
    const componentStack = errorInfo?.componentStack || null;
    this.setState({ errorId, componentStack });
    console.error(`[JobMatch NG Application Error ${errorId}]:`, error, errorInfo);
    void this.submitBugReport(errorId, error, componentStack);
  }

  private async submitBugReport(
    errorId: string,
    error: Error,
    componentStack: string | null,
    additionalNotes?: string
  ) {
    this.setState({ reportStatus: 'sending' });

    try {
      let userId: string | null = null;
      let userEmail: string | null = null;

      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user) {
          userId = data.user.id;
          userEmail = data.user.email || null;
        }
      } catch {
        // Ignore auth lookup errors during crash recovery
      }

      const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
      const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
      const timestamp = new Date().toISOString();
      const notes = (additionalNotes ?? this.state.userNotes).trim() || null;

      const payload = {
        error_id: errorId,
        error_name: error.name || 'Error',
        error_message: error.message || String(error),
        error_stack: error.stack || null,
        component_stack: componentStack,
        url: currentUrl,
        user_agent: userAgent,
        user_id: userId,
        user_email: userEmail,
        user_notes: notes,
        metadata: {
          pathname: typeof window !== 'undefined' ? window.location.pathname : '',
          viewport:
            typeof window !== 'undefined'
              ? `${window.innerWidth}x${window.innerHeight}`
              : undefined,
          reported_at: timestamp,
        },
      };

      const { error: insertError } = await supabase.from('error_reports').insert(payload);

      if (insertError) {
        // Fallback to audit_logs table if error_reports table is not yet provisioned
        const { error: fallbackError } = await supabase.from('audit_logs').insert({
          admin_id: userId,
          admin_name: userEmail || 'Client ErrorBoundary',
          admin_email: userEmail || 'system@jobmatch.ng',
          action: 'CLIENT_CRASH_REPORT',
          target_type: 'error_boundary',
          target_id: errorId,
          target_summary: (error.message || 'Unhandled UI Exception').slice(0, 250),
          details: payload,
          reason: notes || error.name || 'React ErrorBoundary Exception',
        });

        if (fallbackError) {
          throw insertError;
        }
      }

      this.setState({ reportStatus: 'sent' });
    } catch (reportErr) {
      console.warn('[JobMatch NG Bug Reporter] Failed to send error report to Supabase:', reportErr);
      this.setState({ reportStatus: 'failed' });
    }
  }

  render() {
    if (this.state.hasError) {
      const { error, componentStack, errorId, showDetails, reportStatus, userNotes } = this.state;

      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 p-6 text-slate-900 dark:text-slate-100 transition-colors duration-200">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-3xl p-8 border border-slate-200 dark:border-slate-800 shadow-xl text-center space-y-5">
            <div className="w-14 h-14 bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-400 rounded-2xl flex items-center justify-center mx-auto text-2xl font-bold border border-emerald-100 dark:border-emerald-800/60">
              🛠️
            </div>
            <div className="space-y-1">
              <h1 className="text-xl font-extrabold text-slate-900 dark:text-white tracking-tight">JobMatch NG</h1>
              <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                Something went wrong while loading this page.
              </p>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
              We couldn't display this section right now. Please refresh the page or return to the homepage to continue browsing.
            </p>

            {/* Automated Bug Report Status Box */}
            <div className="bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-3.5 text-left space-y-2">
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-bold text-slate-700 dark:text-slate-300">
                  Reference ID:{' '}
                  <span className="font-mono text-slate-900 dark:text-white">{errorId || 'ERR-JM'}</span>
                </span>
                {reportStatus === 'sending' && (
                  <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    Reporting bug...
                  </span>
                )}
                {reportStatus === 'sent' && (
                  <span className="text-[11px] font-semibold text-emerald-700 dark:text-emerald-400">
                    ✓ Bug report logged
                  </span>
                )}
                {reportStatus === 'failed' && (
                  <button
                    type="button"
                    onClick={() => {
                      if (error && errorId) {
                        void this.submitBugReport(errorId, error, componentStack);
                      }
                    }}
                    className="text-[11px] font-bold text-emerald-700 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-300 underline cursor-pointer"
                  >
                    Retry report
                  </button>
                )}
              </div>

              <div className="flex gap-1.5 pt-1">
                <input
                  type="text"
                  value={userNotes}
                  onChange={(e) => this.setState({ userNotes: e.target.value })}
                  placeholder="Optional: Tell us what you were doing..."
                  className="flex-1 px-2.5 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-800 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
                />
                <button
                  type="button"
                  disabled={!userNotes.trim() || reportStatus === 'sending'}
                  onClick={() => {
                    if (error && errorId && userNotes.trim()) {
                      void this.submitBugReport(errorId, error, componentStack, userNotes);
                    }
                  }}
                  className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-slate-800 dark:hover:bg-slate-700 disabled:opacity-40 text-white text-[11px] font-semibold rounded-lg transition cursor-pointer"
                >
                  Send Note
                </button>
              </div>
            </div>

            {error && (
              <div className="text-left">
                <button
                  type="button"
                  onClick={() => this.setState({ showDetails: !showDetails })}
                  className="text-[11px] font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 underline cursor-pointer"
                >
                  {showDetails ? 'Hide technical details' : 'Show technical details'}
                </button>
                {showDetails && (
                  <pre className="mt-2 p-3 bg-slate-900 text-slate-100 rounded-xl text-[10px] font-mono overflow-x-auto max-h-36 whitespace-pre-wrap break-words border border-slate-800">
                    {error.name}: {error.message}
                    {componentStack ? `\n${componentStack}` : ''}
                  </pre>
                )}
              </div>
            )}

            <div className="flex flex-col gap-2 pt-2">
              <button
                onClick={() => {
                  window.location.reload();
                }}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition shadow-sm cursor-pointer"
              >
                Reload Page
              </button>
              <button
                onClick={() => {
                  window.location.href = '/';
                }}
                className="px-6 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 font-semibold rounded-xl text-xs transition cursor-pointer"
              >
                Return to Homepage
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
